/**
 * FAQ Data
 * Organized by user type and category
 */

export interface FAQItem {
  question: string
  answer: string
  category: string
}

export interface FAQCategory {
  id: string
  name: string
  description: string
  items: FAQItem[]
}

export const faqData: FAQCategory[] = [
  {
    id: 'general',
    name: 'General',
    description: 'Common questions about the platform',
    items: [
      {
        question: 'What is LandLordNoAgent?',
        answer: 'LandLordNoAgent is a rental platform that connects landlords directly with tenants, eliminating the need for real estate agents. We provide tools for property listing, application management, secure payments, and communication.',
        category: 'general',
      },
      {
        question: 'How does the payment/escrow system work?',
        answer: 'Payments are securely processed through our escrow system. When a tenant pays rent, the funds are held in escrow until the landlord confirms receipt. The platform deducts a commission (configurable by admin, typically 10%) before releasing funds to the landlord. This ensures secure transactions for both parties.',
        category: 'general',
      },
      {
        question: 'Is the platform free to use?',
        answer: 'For tenants, the platform is completely free. Landlords pay a commission on successful transactions (configurable by admin, typically 10%), which is automatically deducted from rental payments. There are no upfront fees or subscription costs.',
        category: 'general',
      },
      {
        question: 'How long does property verification take?',
        answer: 'Property verification typically takes 24-48 hours after submission. Our admin team reviews property details, images, and required documents to ensure accuracy and quality.',
        category: 'general',
      },
    ],
  },
  {
    id: 'client',
    name: 'For Tenants',
    description: 'Questions about renting properties',
    items: [
      {
        question: 'What documents do I need for KYC verification?',
        answer: 'For KYC verification, you need to upload: 1) A valid government-issued ID (Driver\'s License, National ID, or Passport), 2) Proof of income (payslip, bank statement, or employment letter), and 3) A recent passport photograph. These documents help landlords verify your identity and financial capability.',
        category: 'client',
      },
      {
        question: 'How do I apply for a property?',
        answer: 'Browse available properties, click on one you\'re interested in, and click "Apply Now". Fill out the application form with your details, income information, and any additional documents. Once submitted, the landlord will review your application and respond within a few days.',
        category: 'client',
      },
      {
        question: 'What happens if my application is rejected?',
        answer: 'If your application is rejected, you\'ll receive a notification with the reason (if provided by the landlord). You can continue browsing and applying to other properties. Rejected applications don\'t affect your ability to apply for other listings.',
        category: 'client',
      },
      {
        question: 'How do I schedule a property viewing?',
        answer: 'After submitting an application, you can request a viewing appointment directly from the application page. The landlord will be notified and can confirm or suggest alternative times. Viewing appointments are coordinated through the messaging system.',
        category: 'client',
      },
      {
        question: 'Can I save properties for later?',
        answer: 'Yes! Click the heart icon on any property card to save it to your favorites. You can access all saved properties from the "Saved Properties" section in your dashboard.',
        category: 'client',
      },
      {
        question: 'How does the payment process work?',
        answer: 'Once your application is approved, you\'ll see a payment button on the application page. Click it to securely process your rent payment through our payment gateway. Funds are held in escrow until the landlord confirms receipt.',
        category: 'client',
      },
    ],
  },
  {
    id: 'landlord',
    name: 'For Landlords',
    description: 'Questions about listing and managing properties',
    items: [
      {
        question: 'How do I list my property?',
        answer: 'Click "Add Property" from your dashboard, fill in property details (title, description, price, location, amenities), upload at least 5 high-quality images, and submit. Your property will be reviewed by our admin team and verified within 24-48 hours before going live.',
        category: 'landlord',
      },
      {
        question: 'What documents do I need for property verification?',
        answer: 'You need to upload property verification documents such as: Property deed, Certificate of Occupancy, or lease agreement. You also need at least 5 clear images of the property showing different rooms and the exterior.',
        category: 'landlord',
      },
      {
        question: 'How do I review applications?',
        answer: 'Applications appear in your dashboard under "Applications". Click on any application to view tenant details, income information, KYC status, and messages. You can approve, reject, or request more information from the tenant.',
        category: 'landlord',
      },
      {
        question: 'When do I receive payment?',
        answer: 'Once a tenant makes a payment, it\'s held in escrow. You\'ll receive a notification when payment is available. After confirming receipt, funds (minus platform commission, typically 10%) are processed to your account within 3-5 business days.',
        category: 'landlord',
      },
      {
        question: 'Can I edit my property listing after it\'s verified?',
        answer: 'Yes, you can edit most property details. However, verified properties may have some restrictions. If you need to make significant changes, contact support for assistance.',
        category: 'landlord',
      },
      {
        question: 'How do I communicate with tenants?',
        answer: 'Use the built-in messaging system accessible from any application page. You can exchange messages, share documents, and coordinate viewing appointments all within the platform.',
        category: 'landlord',
      },
    ],
  },
  {
    id: 'account',
    name: 'Account & Security',
    description: 'Questions about account management and security',
    items: [
      {
        question: 'How do I update my profile information?',
        answer: 'Go to your Profile section in the dashboard. Click "Edit Profile" to update your personal information, contact details, and profile picture. Remember to save your changes.',
        category: 'account',
      },
      {
        question: 'What should I do if I forgot my password?',
        answer: 'Click "Forgot Password" on the login page, enter your email address, and check your inbox for a 6-digit reset code. Go to the reset password page, enter the code and your email, then create a new password.',
        category: 'account',
      },
      {
        question: 'How do I verify my account?',
        answer: 'Complete KYC verification by uploading required documents in your Profile section. Our admin team will review your documents and verify your account, typically within 24-48 hours.',
        category: 'account',
      },
      {
        question: 'Is my personal information secure?',
        answer: 'Yes, we use industry-standard encryption and security measures to protect your data. Your financial information is processed through secure payment gateways, and we never share your personal information with third parties without your consent.',
        category: 'account',
      },
    ],
  },
]

/**
 * Get FAQs by category
 */
export function getFAQsByCategory(categoryId: string): FAQItem[] {
  const category = faqData.find(cat => cat.id === categoryId)
  return category ? category.items : []
}

/**
 * Get all FAQs
 */
export function getAllFAQs(): FAQItem[] {
  return faqData.flatMap(category => category.items)
}

/**
 * Search FAQs by query
 */
export function searchFAQs(query: string): FAQItem[] {
  const lowerQuery = query.toLowerCase()
  return getAllFAQs().filter(item =>
    item.question.toLowerCase().includes(lowerQuery) ||
    item.answer.toLowerCase().includes(lowerQuery)
  )
}

